/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { find, first, includes } from 'lodash';

import {
  addLineItemNote,
  listLineItemNotes,
  updateAddOnQuantity,
  updateLineItemStatus,
  updateNoteStatus,
} from 'store/thunks/patientThunks';
import { patientActions } from 'store/slices/patientSlice';
import useRoles from 'common/hooks/useRoles';
import useInfoDialog from 'common/hooks/useInfoDialog';

import { ContextType } from 'common/constants/notes';
import lineItemStatus from 'enums/lineItem.enum';
import orderStatus from 'enums/order.enum';
import processingStateEnum from 'enums/processingState.enum';
import { CENTRAL_FILL_REVIEW_REASONS } from 'enums/prescription.enum';
import AddNoteDialog from 'common/modules/notes/AddNoteDialog';
import NotesDialog from 'common/modules/notes/NotesDialog';
import { LoadingButton } from '@mui/lab';
import { updateAddressOrderLineItemStatus } from 'store/thunks/batchShipmentThunk';
import { upsertLineItemTags } from 'store/thunks/tagThunks';
import { applyUrgentTag, removeUrgentTag } from 'store/thunks/workQueueThunks';
import { DISABLED_TAGS_STATUSES } from 'common/constants/orderStatusGroups';
import PatientBanner from 'common/modules/PatientBanner';
import ProductFulfillFailureReasonDialog from './ProductFulfillFailureReasonDialog';
import CancelLineItemDialog from './CancelLineItemDialog';
import ManageTagsDialog from './ManageTagsDialog';

const PatientNoteOptions = ({
  itemId,
  mpi,
  orderId,
  rxNumber,
  itemStatus,
  processingState,
  activeNoteItem,
  setActiveNoteItem,
  addressDirectoryId,
  inBatchShipmentQueue,
  isReshipmentOrder,
  addOnQuantity,
  isMarkedForFillLater,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showCancelLineItemDialog, setShowCancelLineItemDialog] = useState(false);
  const [showProductFillFailureDialog, setShowProductFillFailureDialog] = useState(false);
  const [showAddToBatchShippingDialog, setShowAddToBatchShippingDialog] = useState(false);
  const [showManageTagsDialog, setShowManageTagsDialog] = useState(false);
  const [showEditAddOnDialog, setShowEditAddOnDialog] = useState(false);
  const [showEditAddOnConfirmDialog, setShowEditAddOnConfirmDialog] = useState(false);
  const [editAddOnQuantity, setEditAddOnQuantity] = useState('');
  const [isAddOnLoading, setIsAddOnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasAccess = useRoles();
  const dispatch = useDispatch();
  const orders = useSelector(({ patient }) => patient[mpi]?.orders || []);
  const orderHistory = useSelector(({ patient }) => patient[mpi]?.orderHistory || []);
  const prescriptions = useSelector(({ patient }) => patient[mpi]?.prescriptions || []);
  const patientOrders = [...orders, ...orderHistory];
  const orderLineItems = find(patientOrders, { orderId })?.lineItems || [];
  const isProcessedExternal = find(patientOrders, { orderId })?.isProcessedExternal;
  const currentLineItem = find(orderLineItems, { lineItemId: itemId });
  const currentPrescription = find(prescriptions, {
    prescriptionUUID: currentLineItem?.prescriptionId,
  });
  const lineItemNotes = currentLineItem?.notes;
  const currentLineItemTags = currentLineItem?.tags || [];
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const { enqueueSnackbar } = useSnackbar();
  const currentOrderStatus = find(patientOrders, { orderId })?.status;
  const orderStatusesAllowsUndo = ['OPEN', 'ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'];
  const menuOpen = !!anchorEl;
  const isActiveLineItem = processingState === 'PROCESSING';
  const isCentralFillLineItem = includes(
    CENTRAL_FILL_REVIEW_REASONS,
    currentLineItem?.reviewReason
  );
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const handleOpenAddToBatchShippingModal = () => {
    setShowAddToBatchShippingDialog(true);
    handleMenuClose();
  };

  const handleCloseAddToshippingModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowAddToBatchShippingDialog(false);
    }
  };

  const handleShowNoteDialog = () => {
    setAnchorEl(null);
    setShowAddNoteDialog(true);
  };

  const handleViewNoteDialog = useCallback(() => {
    setShowNotesDialog(true);
    setAnchorEl(null);

    dispatch(
      listLineItemNotes({
        mpi,
        lineItemId: itemId,
        vaultId: orderId,
        vaultType: ContextType.LINE_ITEM,
      })
    );
  }, [dispatch, mpi, itemId, orderId]);

  useEffect(() => {
    if (activeNoteItem === itemId) {
      handleViewNoteDialog();
      setActiveNoteItem(null);
    }
  }, [activeNoteItem, itemId, handleViewNoteDialog, setActiveNoteItem]);

  const closeViewNoteDialog = () => setShowNotesDialog(false);

  const handleCloseNoteDialog = () => setShowAddNoteDialog(false);

  const handleCloseFailureReasonDialog = () => setShowProductFillFailureDialog(false);

  const handleLineItemCancelDialog = () => setShowCancelLineItemDialog(!showCancelLineItemDialog);

  const handleCannotFill = () => {
    setShowProductFillFailureDialog(true);
    setAnchorEl(null);
  };

  const handleUndoFilledProduct = () => {
    setAnchorEl(null);

    showInfoDialog({
      title: 'Undo Product Fulfilled',
      message: 'Are you sure, Do you need to undo Product Fulfilled',
      callback: () =>
        dispatch(
          updateLineItemStatus({
            lineItemId: itemId,
            mpi,
            orderId,
            state: lineItemStatus.PRODUCT_FULFILLMENT,
          })
        ).then((isUndone) => {
          if (isUndone) {
            enqueueSnackbar('Line Item undone to Product Fulfillment', { variant: 'success' });
          } else {
            enqueueSnackbar('Failed to undone to Product Fulfillment', { variant: 'error' });
          }
        }),
      cancelable: true,
      confirmButtonText: 'Confirm',
    });
  };

  const onAddNote = (commentContext, content, onComplete) => {
    return dispatch(
      addLineItemNote({
        mpi,
        commentContext,
        content,
        vaultId: orderId,
        vaultType: ContextType.LINE_ITEM,
      })
    ).then(() => {
      onComplete(true);
    });
  };

  const onUpdateNoteStatus = (noteId, action) => {
    return dispatch(
      updateNoteStatus({
        lineItemId: itemId,
        mpi,
        noteId,
        vaultId: orderId,
        vaultType: ContextType.LINE_ITEM,
        action,
      })
    );
  };

  const handleAddToBatchShipping = () => {
    setIsLoading(true);
    dispatch(
      updateAddressOrderLineItemStatus({
        addressDirectoryId,
        lineItemId: itemId,
        status: 'IN_BATCH_SHIPPING',
        mpi,
      })
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar('Item added to batch shipment', { variant: 'success' });
          setIsLoading(false);
        }
      })
      .finally(() => {
        setShowAddToBatchShippingDialog(false);
      });
  };

  const handleOpenEditAddOnDialog = () => {
    setEditAddOnQuantity(addOnQuantity != null ? String(addOnQuantity) : '');
    setShowEditAddOnDialog(true);
    handleMenuClose();
  };

  const handleCloseEditAddOnDialog = () => {
    setShowEditAddOnDialog(false);
    setEditAddOnQuantity('');
  };

  const handleEditAddOnSubmit = () => {
    setShowEditAddOnDialog(false);
    setShowEditAddOnConfirmDialog(true);
  };

  const handleEditAddOnConfirm = () => {
    setIsAddOnLoading(true);
    dispatch(
      updateAddOnQuantity({
        lineItemId: itemId,
        orderId,
        mpi,
        quantity: Number(editAddOnQuantity),
      })
    )
      .then((success) => {
        if (success) {
          enqueueSnackbar('Add On quantity updated successfully', { variant: 'success' });
        } else {
          enqueueSnackbar('Failed to update Add On quantity', { variant: 'error' });
        }
      })
      .finally(() => {
        setIsAddOnLoading(false);
        setShowEditAddOnConfirmDialog(false);
        setEditAddOnQuantity('');
      });
  };

  const handleOpenManageTagsDialog = () => {
    setShowManageTagsDialog(true);
    handleMenuClose();
  };

  const handleCloseManageTagsDialog = () => {
    setShowManageTagsDialog(false);
  };

  const handleSaveTags = async ({ lineItemId, tags: newTags, urgentShipByDate }) => {
    const currentTagNames = (currentLineItemTags || []).map((t) => t.tagName);
    const newTagNames = (newTags || []).map((t) => t.tagName);
    const urgentAdded = newTagNames.includes('URGENT') && !currentTagNames.includes('URGENT');
    const urgentRemoved = !newTagNames.includes('URGENT') && currentTagNames.includes('URGENT');
    const urgentReAdded =
      Boolean(urgentShipByDate) &&
      newTagNames.includes('URGENT') &&
      currentTagNames.includes('URGENT');

    if (!urgentAdded && !urgentRemoved && !urgentReAdded) {
      return dispatch(
        upsertLineItemTags({ lineItemId, currentTags: currentLineItemTags, newTags })
      ).then((response) => {
        const updatedTags = first(response?.updatedLineItemTags)?.tags;
        dispatch(
          patientActions.updateLineItemTags({ mpi, orderId, lineItemId, tags: updatedTags })
        );
        handleCloseManageTagsDialog();
        return !!response?.updatedLineItemTags;
      });
    }

    // URGENT tag was added, removed, or re-added with a new ship by date
    let urgentResult;
    if (urgentAdded || urgentReAdded) {
      urgentResult = await dispatch(
        applyUrgentTag({
          lineItemId,
          mpi,
          orderId,
          shipByDate: urgentShipByDate,
        })
      );
    } else {
      urgentResult = await dispatch(removeUrgentTag({ lineItemId, mpi, orderId }));
    }

    // Also handle any concurrent non-URGENT tag changes
    const nonUrgentCurrentTags = (currentLineItemTags || []).filter((t) => t.tagName !== 'URGENT');
    const nonUrgentNewTags = (newTags || []).filter((t) => t.tagName !== 'URGENT');
    const nonUrgentCurrentNames = nonUrgentCurrentTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    const nonUrgentNewNames = nonUrgentNewTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    let updatedTags = urgentResult?.tags;
    if (nonUrgentCurrentNames !== nonUrgentNewNames) {
      const tagResponse = await dispatch(
        upsertLineItemTags({
          lineItemId,
          currentTags: nonUrgentCurrentTags,
          newTags: nonUrgentNewTags,
        })
      );
      updatedTags = first(tagResponse?.updatedLineItemTags)?.tags ?? updatedTags;
    }

    dispatch(patientActions.updateLineItemTags({ mpi, orderId, lineItemId, tags: updatedTags }));
    handleCloseManageTagsDialog();
    return true;
  };

  return (
    <>
      <InfoDialog />
      <AddNoteDialog
        commentContexType={ContextType.LINE_ITEM}
        commentContextId={itemId}
        open={showAddNoteDialog}
        onClose={handleCloseNoteDialog}
        onAddNote={onAddNote}
      />

      <NotesDialog
        open={showNotesDialog}
        onClose={closeViewNoteDialog}
        itemNotes={lineItemNotes}
        onUpdateNoteStatus={onUpdateNoteStatus}
      />

      <ProductFulfillFailureReasonDialog
        open={showProductFillFailureDialog}
        onClose={handleCloseFailureReasonDialog}
        lineItemId={itemId}
        mpi={mpi}
        orderId={orderId}
      />

      <CancelLineItemDialog
        mpi={mpi}
        orderId={orderId}
        rxNumber={rxNumber}
        lineItemId={itemId}
        hasFillLater={isMarkedForFillLater}
        open={showCancelLineItemDialog}
        onClose={handleLineItemCancelDialog}
      />

      <Dialog
        open={showAddToBatchShippingDialog}
        aria-describedby='BatchShipment-RemoveItemFromSHipment-Header'
        fullWidth
      >
        <DialogTitle id='BatchShipment-RemoveItemFromSHipment-Header' sx={{ pb: 0 }}>
          Confirm Batch shipping
        </DialogTitle>

        <DialogContent>
          <Typography variant='subtitle1' sx={{ m: 2 }}>
            Please confirm to add this item to batch shipping
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseAddToshippingModal}
            variant='outlined'
            color='secondary'
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            color='primary'
            loading={isLoading}
            onClick={handleAddToBatchShipping}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ManageTagsDialog
        open={showManageTagsDialog}
        onClose={handleCloseManageTagsDialog}
        mpi={mpi}
        orderId={orderId}
        lineItemId={itemId}
        existingTags={currentLineItemTags}
        onSave={handleSaveTags}
      />

      <Dialog
        open={showEditAddOnDialog}
        onClose={handleCloseEditAddOnDialog}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Edit Add On Quantity</DialogTitle>
        <DialogContent>
          <PatientBanner mpi={mpi} />
          <Box
            sx={{
              mt: 2,
              mb: 2,
              p: 1.5,
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                mt: 1,
              }}
            >
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Drug Name
                </Typography>
                <Typography variant='body2' fontWeight={500}>
                  {currentLineItem?.itemName || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Strength
                </Typography>
                <Typography variant='body2' fontWeight={500}>
                  {currentLineItem?.strength || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Form
                </Typography>
                <Typography variant='body2' fontWeight={500}>
                  {currentLineItem?.form || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Sig
                </Typography>
                <Typography variant='body2' fontWeight={500}>
                  {currentPrescription?.sig || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant='body2' sx={{ mb: 2 }}>
            Current Add On Quantity: <strong>{addOnQuantity}</strong>
          </Typography>
          <TextField
            label='New Quantity'
            type='number'
            fullWidth
            value={editAddOnQuantity}
            onChange={(e) => setEditAddOnQuantity(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === '.' || e.key === 'e' || e.key === 'E') {
                e.preventDefault();
              }
            }}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditAddOnDialog} variant='outlined' color='secondary'>
            Cancel
          </Button>
          <Button
            onClick={handleEditAddOnSubmit}
            variant='contained'
            color='primary'
            disabled={
              !editAddOnQuantity ||
              Number(editAddOnQuantity) <= 0 ||
              Number(editAddOnQuantity) === addOnQuantity
            }
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditAddOnConfirmDialog} fullWidth maxWidth='xs'>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography variant='subtitle1'>
            Are you sure you want to update the Add On quantity to{' '}
            <strong>{editAddOnQuantity}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditAddOnConfirmDialog(false);
              setShowEditAddOnDialog(true);
            }}
            variant='outlined'
            color='secondary'
            disabled={isAddOnLoading}
          >
            No
          </Button>
          <LoadingButton
            onClick={handleEditAddOnConfirm}
            variant='contained'
            color='primary'
            loading={isAddOnLoading}
          >
            Yes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <IconButton
        id={`PatientView-PatientNoteOptions-${itemId}options-button`}
        aria-label={`Note options ${itemId}`}
        aria-controls={
          menuOpen ? `PatientView-PatientNoteOptions-${itemId}options-menu` : undefined
        }
        aria-haspopup='true'
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={handleMenuOpen}
        disabled={itemStatus === 'SPLIT'}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={`PatientView-PatientNoteOptions-${itemId}options-menu`}
        aria-labelledby={`PatientView-PatientNoteOptions-${itemId}options-button`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleShowNoteDialog} disabled={!hasAccess.addPatientNote}>
          Add Note
        </MenuItem>
        <MenuItem onClick={handleViewNoteDialog} disabled={!hasAccess.listLineItemNotes}>
          View Notes
        </MenuItem>
        <MenuItem
          disabled={
            DISABLED_TAGS_STATUSES.includes(processingState) ||
            currentOrderStatus === orderStatus.EXPIRED
          }
          onClick={handleOpenManageTagsDialog}
        >
          Manage Tags
        </MenuItem>
        <MenuItem
          disabled={
            !isActiveLineItem ||
            isProcessedExternal ||
            itemStatus === lineItemStatus.PRODUCT_FULFILLMENT ||
            !hasAccess.manageProductFulfillmentStatus ||
            !includes(orderStatusesAllowsUndo, currentOrderStatus)
          }
          onClick={handleUndoFilledProduct}
        >
          Undo
        </MenuItem>
        {orderLineItems.length !== 1 && (
          <MenuItem
            disabled={
              !isActiveLineItem ||
              isProcessedExternal ||
              !hasAccess.manageProductFulfillmentStatus ||
              currentOrderStatus === orderStatus.ORDER_VERIFIED ||
              itemStatus === lineItemStatus.PRODUCT_FULFILLED ||
              isCentralFillLineItem
            }
            onClick={handleCannotFill}
          >
            Cannot Fill
          </MenuItem>
        )}

        {addressDirectoryId &&
          itemId &&
          !inBatchShipmentQueue &&
          processingState === 'PROCESSING' &&
          !isReshipmentOrder &&
          currentOrderStatus !== orderStatus.EXPIRED &&
          hasAccess.viewBatchShipments && (
            <MenuItem onClick={handleOpenAddToBatchShippingModal} disabled={isCentralFillLineItem}>
              Add to batch shipping
            </MenuItem>
          )}

        {addOnQuantity != null &&
          hasAccess.updateAddOnQuantity &&
          currentOrderStatus !== orderStatus.CANCELED &&
          currentOrderStatus !== orderStatus.SHIPPED &&
          currentOrderStatus !== orderStatus.EXPIRED &&
          itemStatus !== lineItemStatus.CANCELED &&
          itemStatus !== lineItemStatus.SPLIT &&
          processingState !== processingStateEnum.SHIPPED &&
          processingState !== processingStateEnum.IN_SHIPMENT && (
            <MenuItem onClick={handleOpenEditAddOnDialog}>Edit Add-On</MenuItem>
          )}
        <MenuItem
          onClick={handleLineItemCancelDialog}
          disabled={
            !isActiveLineItem ||
            !hasAccess.cancelOrder ||
            currentOrderStatus === orderStatus.EXPIRED ||
            isCentralFillLineItem
          }
        >
          Cancel
        </MenuItem>
      </Menu>
    </>
  );
};

PatientNoteOptions.propTypes = {
  itemId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  rxNumber: PropTypes.string.isRequired,
  itemStatus: PropTypes.string.isRequired,
  activeNoteItem: PropTypes.string,
  setActiveNoteItem: PropTypes.func,
  processingState: PropTypes.string,
  addressDirectoryId: PropTypes.string,
  inBatchShipmentQueue: PropTypes.bool,
  isReshipmentOrder: PropTypes.bool,
  addOnQuantity: PropTypes.number,
  isMarkedForFillLater: PropTypes.bool,
};

PatientNoteOptions.defaultProps = {
  activeNoteItem: null,
  setActiveNoteItem: undefined,
  processingState: 'PROCESSING',
  addressDirectoryId: undefined,
  inBatchShipmentQueue: false,
  isReshipmentOrder: false,
  addOnQuantity: null,
  isMarkedForFillLater: false,
};

export default PatientNoteOptions;
